import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { connect } from 'react-redux';

/**
 * TenantSelector - Dropdown for users with multiple tenant memberships
 * Shows current active tenant and allows switching between tenants
 */
const TenantSelector = ({ auth }) => {
  const [tenants, setTenants] = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (auth && auth.id) {
      fetchTenants();
    }
  }, [auth]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await fetch('/api/user/tenants', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTenants(data.tenants || []);
        const active = data.tenants?.find(t => t.is_active) || data.tenants?.[0];
        setActiveTenant(active);
      }
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchTenant = async (tenantId) => {
    try {
      // Find the tenant being switched to
      const targetTenant = tenants.find(t => t.id === tenantId);
      const isPlatformAdmin = auth && (auth.platform_role === 'super_admin' || auth.platform_role === 'admin');

      const response = await fetch('/api/user/tenants/active', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenant_id: tenantId })
      });

      if (response.ok) {
        // If platform admin switching to platform tenant, go to admin area
        if (isPlatformAdmin && targetTenant?.slug === 'spawnry') {
          window.location.href = '/admin/tenants';
        } else {
          // Otherwise go to the main tool
          window.location.href = '/app/article-to-media';
        }
      }
    } catch (err) {
      console.error('Failed to switch tenant:', err);
    }
  };

  // Don't render if not authenticated or still loading
  if (!auth || !auth.id || loading) {
    return null;
  }

  // If user has no tenants, don't show selector
  if (tenants.length === 0) {
    return null;
  }

  // If user has only one tenant, show static text instead of dropdown
  if (tenants.length === 1) {
    return (
      <div className="d-flex align-items-center px-2" style={{
        fontSize: '0.9rem',
        color: '#64748b'
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <line x1="3" y1="9" x2="21" y2="9"/>
          <line x1="9" y1="21" x2="9" y2="9"/>
        </svg>
        {tenants[0].name}
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="d-flex align-items-center">
      <Dropdown show={showDropdown} onToggle={() => {}}>
        <div
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.35rem 0.75rem',
            borderRadius: '8px',
            cursor: 'pointer',
            background: showDropdown ? '#f1f5f9' : 'transparent',
            transition: 'background 0.2s'
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <line x1="3" y1="9" x2="21" y2="9"/>
            <line x1="9" y1="21" x2="9" y2="9"/>
          </svg>
          <span style={{ fontSize: '0.9rem', color: '#374151', fontWeight: '500', marginRight: '0.3rem' }}>
            {activeTenant?.name || 'Select Tenant'}
          </span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>

        <Dropdown.Menu
          align="end"
          show={showDropdown}
          style={{
            minWidth: '200px',
            padding: '0.5rem',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
          }}
        >
          {tenants.map((tenant) => (
            <Dropdown.Item
              key={tenant.id}
              onClick={() => handleSwitchTenant(tenant.id)}
              style={{
                padding: '0.6rem 0.8rem',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: tenant.is_active ? '#f0fdf4' : 'transparent'
              }}
            >
              <span>{tenant.name}</span>
              {tenant.is_active && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(TenantSelector);
